import { useEffect, useState } from 'react'
import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import Skeleton from '@mui/material/Skeleton'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { lockedFont } from './constants'
import { useProfilePictureUpload } from './hooks/use-profile-picture-upload'
import ProfilePictureTool from './ProfilePictureTool'
import CameraIcon from './assets/cameraIcon.svg?react'
import SpinnerIcon from './assets/spinner.svg?react'

interface ProfileHeaderProps {
    customerName: string
    profilePicUrl: string
    baId: string
    isLoading: boolean
}

const AVATAR_SX = {
    width: 85,
    height: 85,
    borderRadius: '16px',
    border: '2.5px solid white'
} as const

const ProfileHeaderSkeleton = () => (
    <Stack direction="row" alignItems="flex-start" spacing={2} sx={{ p: '40px 40px' }} data-testid="profile-header-stack-skeleton-1">
        <Box sx={{ width: 88, height: 88, flexShrink: 0 }} data-testid="profile-header-box-skeleton">
            <Skeleton
                variant="rounded"
                width={85}
                height={85}
                sx={{
                    borderRadius: '16px',
                    bgcolor: 'rgba(255,255,255,0.22)'
                }}
                data-testid="profile-header-skeleton-1"
            />
        </Box>
        <Stack spacing={1} sx={{ minWidth: 0, flex: 1, pt: '2px' }} data-testid="profile-header-stack-skeleton-2">
            <Skeleton
                variant="rounded"
                height={30}
                width="100%"
                sx={{
                    maxWidth: 224,
                    bgcolor: 'rgba(255,255,255,0.22)'
                }}
                data-testid="profile-header-skeleton-2"
            />
            <Skeleton
                variant="rounded"
                height={24}
                width="45%"
                sx={{
                    maxWidth: 120,
                    bgcolor: 'rgba(255,255,255,0.18)'
                }}
                data-testid="profile-header-skeleton-3"
            />
        </Stack>
    </Stack>
)

const ProfileHeader = ({ customerName, profilePicUrl, baId, isLoading }: ProfileHeaderProps) => {
    const { inputRef, uploading, error, pendingSrc, cropOpen, openFilePicker, handleFileChange, handleCropConfirm, closeCropTool } = useProfilePictureUpload()
    // Match legacy UX: when a profile picture already exists, the camera badge
    // only appears after the first click. Without a picture, it shows immediately.
    const [showCameraIcon, setShowCameraIcon] = useState(!profilePicUrl)

    // After a successful upload, the picture URL changes — re-hide the badge so
    // the next interaction once again requires the two-click reveal.
    useEffect(() => {
        if (profilePicUrl) setShowCameraIcon(false)
    }, [profilePicUrl])

    if (isLoading) return <ProfileHeaderSkeleton data-testid="profile-header-profile-header-skeleton" />

    const handleAvatarClick = () => {
        if (uploading) return
        if (!showCameraIcon) {
            setShowCameraIcon(true)
            return
        }
        openFilePicker()
    }

    return (
        <>
            <input
                ref={inputRef}
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                style={{ display: 'none' }}
                data-testid="profile-header-input-file"
            />
            <Stack direction="row" alignItems="flex-start" spacing={2} sx={{ p: '40px 40px' }} data-testid="profile-header-stack-handle-avatar-click">
                <Box
                    role="button"
                    tabIndex={0}
                    onClick={handleAvatarClick}
                    onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            handleAvatarClick()
                        }
                    }}
                    sx={{
                        position: 'relative',
                        width: 88,
                        height: 88,
                        cursor: uploading ? 'wait' : 'pointer',
                        flexShrink: 0,
                        outline: 'none'
                    }}
                    data-testid="profile-header-box-handle-avatar-click">
                    <Avatar
                        src={profilePicUrl}
                        alt=""
                        variant="rounded"
                        sx={{
                            ...AVATAR_SX,
                            bgcolor: '#BDBDC1',
                            boxShadow: '0px 1px 3px rgba(68,68,78,0.5)',
                            fontSize: 28,
                            fontWeight: 600,
                            color: '#FFFFFF',
                            opacity: uploading ? 0.5 : 1
                        }}
                        data-testid="profile-header-avatar">
                        {customerName.charAt(0).toUpperCase() || '?'}
                    </Avatar>

                    {uploading && (
                        <Box
                            sx={{
                                position: 'absolute',
                                inset: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                            data-testid="profile-header-box-1">
                            <Box
                                component={SpinnerIcon}
                                sx={{
                                    width: 35,
                                    height: 35,
                                    animation: 'profilePicSpin 1s linear infinite',
                                    '@keyframes profilePicSpin': {
                                        from: { transform: 'rotate(0deg)' },
                                        to: { transform: 'rotate(360deg)' }
                                    }
                                }}
                                data-testid="profile-header-box-2"
                            />
                        </Box>
                    )}
                    {!uploading && showCameraIcon && (
                        <Box
                            component={CameraIcon}
                            sx={{
                                position: 'absolute',
                                bottom: -5,
                                right: -5,
                                width: 25,
                                height: 25,
                                display: 'block'
                            }}
                            data-testid="profile-header-box-3"
                        />
                    )}
                </Box>

                <Stack spacing={0.5} sx={{ minWidth: 0, flex: 1 }} data-testid="profile-header-stack-typography">
                    <Typography
                        sx={{
                            ...lockedFont('24px', '30px'),
                            fontWeight: 600,
                            color: '#FFFFFF',
                            overflowWrap: 'anywhere'
                        }}
                        data-testid="profile-header-typography-1">
                        {customerName || '—'}
                    </Typography>
                    <Typography sx={{ ...lockedFont('18px', '24px'), color: '#FFFFFF', opacity: 0.9 }} data-testid="profile-header-typography-2">
                        {baId}
                    </Typography>
                    {error && (
                        <Typography sx={{ ...lockedFont('13px', '18px'), color: '#FF7A7A' }} data-testid="profile-header-typography-3">
                            {error}
                        </Typography>
                    )}
                </Stack>
            </Stack>
            <ProfilePictureTool
                open={cropOpen}
                imgSrc={pendingSrc}
                uploading={uploading}
                onCancel={closeCropTool}
                onConfirm={handleCropConfirm}
                data-testid="profile-header-profile-picture-tool"
            />
        </>
    )
}

export default ProfileHeader
